import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Rating, Grid } from '@mui/material'
import { Typography } from '@material-ui/core'
import Svg from '_components/svg'
import WarningBlueIcon from '_assets/icons/ic-warning-blue.svg'
import { LEVEL_OPTIONS } from '_/views/provider/components/ProviderReferaDetailsModal/constants'
import Tooltip from '_/components/tooltip'

import useStyles from './styles'
import { PageHeader } from './PageHeader'
import { Datagrid } from '@refera/ui-web'
import { confirmationMenuTypes, leadProviderListColumns } from './constants'
import { getLeadProviders } from '_/modules/provider/actions'
import { TABLE_SX } from '_/views/finance/utils/constants'
import { useDispatch } from 'react-redux'

import humps from 'humps'
import { ListLeadProvidersFilters } from './ListLeadProvidersFilters'
import { ListLeadProvidersFloatingMenu } from './ListLeadProvidersFloatingMenu'
import { ImportProvidersConfirmationScreen } from './ImportProvidersConfirmationScreen'
import Button from '_components/button'
import { WhatsApp } from '@material-ui/icons'
import { openWhatsAppWithMessage } from '_/utils/open-whatsApp'

const formatFiltersPayload = filters => {
  const payload = { ...filters }

  if (payload?.mainServices) {
    payload.mainServices = payload.mainServices.map(service => service.id).join()
  }

  return payload
}

export default function ListLeadProviders() {
  const styles = useStyles()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(true)
  const [leadProviders, setLeadProviders] = useState([])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [orderBy, setOrderBy] = useState('name')
  const [menuType, setMenuType] = useState('default')
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [filters, setFilters] = useState({})
  const [selectedItems, setSelectedItems] = useState([])

  const handleOrderBy = useCallback(orderObj => {
    const order = orderObj[0]
    if (!order) {
      handleOrderBy('name')
      return
    }
    const { field, sort } = order
    const decamelizedField = humps.decamelize(field)
    sort === 'desc' ? setOrderBy(`-${decamelizedField}`) : setOrderBy(decamelizedField)
  }, [])

  const handleGetLeadProviders = useCallback(
    (filterParams = null) => {
      const formattedPayload = formatFiltersPayload(filterParams || filters)

      dispatch(getLeadProviders({ ...formattedPayload, page: page + 1, pageSize, orderBy }))
        .then(data => {
          setLeadProviders(data?.results || [])
          setIsLoading(false)
        })
        .catch(() => setIsLoading(false))

      if (filterParams) {
        setFilters(filterParams)
      }
    },
    [page, pageSize, orderBy, filters]
  )

  const handleFloatingModalNavigation = useCallback(type => {
    if (confirmationMenuTypes.includes(type)) setMenuType(type)
  }, [])

  useEffect(() => {
    handleGetLeadProviders()
  }, [page, pageSize, orderBy])

  const renderFilters = useMemo(
    () => (
      <ListLeadProvidersFilters
        open={filterDrawerOpen}
        close={() => setFilterDrawerOpen(false)}
        applyFilters={handleGetLeadProviders}
        filters={filters}
        setFilters={setFilters}
      />
    ),
    [filterDrawerOpen, filters, handleGetLeadProviders]
  )

  const handleGoBackAndUpdate = useCallback(
    ({ update = false }) => {
      setMenuType('default')

      if (update) {
        handleGetLeadProviders()
        setSelectedItems([])
      }
    },
    [menuType, handleGetLeadProviders]
  )

  const renderConfirmationScreen = useMemo(() => {
    if (menuType === 'import') {
      return (
        <ImportProvidersConfirmationScreen
          menuType={menuType}
          selectedIds={selectedItems}
          providers={leadProviders}
          goBack={handleGoBackAndUpdate}
        />
      )
    }
    return null
  }, [menuType, selectedItems, leadProviders, handleGoBackAndUpdate])

  const getLevelLabel = useCallback(level => {
    return LEVEL_OPTIONS.find(itemLevel => itemLevel?.value === level)?.label || ''
  }, [])

  const ProviderEvaluationContent = useCallback(
    ({ params }) => {
      const item = params.row
      const score = item?.score || {}
      const level = item?.level || score?.level
      const servicesCount = item?.servicesCount || score?.servicesCount
      const warrantyPercentage = item?.warrantyPercentage || score?.warrantyPercentage
      const budgetConversion = item?.budgetConversion || score?.budgetConversion

      const hasData = Object.values({
        level,
        servicesCount,
        warrantyPercentage,
        budgetConversion,
      }).some(v => v !== null && v !== undefined && v !== '')

      if (!hasData) {
        return (
          <Tooltip
            title="Prestador não realizou serviços o suficiente com a Refera para gerar classificação."
            placement="top"
          >
            <Typography variant="body2" className={styles.noDataText}>
              Prestador não realizou serviços o suficiente com a Refera para gerar classificação.
            </Typography>
          </Tooltip>
        )
      }

      return (
        <Grid>
          <Tooltip
            triggerType="click"
            title={
              <>
                <Typography variant="body2">
                  <strong>Classificação:</strong> {getLevelLabel(level) || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>Qtd. de serviços realizados:</strong> {servicesCount ?? '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>% de garantias:</strong> {warrantyPercentage ?? '-'}%
                </Typography>
                <Typography variant="body2">
                  <strong>Conv. de orçamentos:</strong> {budgetConversion ?? '-'}%
                </Typography>
              </>
            }
            tooltipClass={styles.tooltip}
          >
            <Typography className={styles.tooltipTrigger}>
              <Svg icon={WarningBlueIcon} className={styles.supportIcon} />
              <span className={styles.link}>Avaliação</span>
            </Typography>
          </Tooltip>
        </Grid>
      )
    },
    [
      getLevelLabel,
      styles.noDataText,
      styles.tooltip,
      styles.tooltipTrigger,
      styles.supportIcon,
      styles.link,
    ]
  )

  const RatingComponent = useCallback(({ params }) => {
    return (
      <Button
        onClick={e => {
          e.stopPropagation()
        }}
        style={{ cursor: 'default' }}
      >
        <Rating value={params.value ?? 0} precision={0.5} readOnly size="large" />
      </Button>
    )
  }, [])

  const WhatsappButton = useCallback(
    ({ phone }) => (
      <Button
        variant="default"
        title="Enviar mensagem de WhatsApp"
        onClick={e => {
          e.stopPropagation()
          openWhatsAppWithMessage({ phone })
        }}
      >
        <WhatsApp className={styles.iconWhatsApp} />
      </Button>
    ),
    [styles.iconWhatsApp]
  )

  return (
    <main className={styles.container}>
      {menuType === 'default' && (
        <>
          {renderFilters}
          <PageHeader isLoading={isLoading} setFilterDrawerOpen={setFilterDrawerOpen} />

          <Grid className={styles.tableContainer}>
            <Grid className={styles.mainTable}>
              <Datagrid
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'name', sort: 'asc' }],
                  },
                }}
                onSortModelChange={handleOrderBy}
                loading={isLoading}
                rows={leadProviders}
                columns={leadProviderListColumns(
                  WhatsappButton,
                  RatingComponent,
                  ProviderEvaluationContent
                )}
                paginationMode="server"
                onPageChange={setPage}
                page={page}
                onPageSizeChange={setPageSize}
                pageSize={pageSize}
                rowCount={leadProviders.length || 0}
                sx={TABLE_SX}
                noRowsLabel="Nenhum resultado encontrado"
                checkboxSelection
                onSelectionModelChange={setSelectedItems}
                selectionModel={selectedItems}
              />
            </Grid>
          </Grid>
          {selectedItems?.length > 0 && (
            <ListLeadProvidersFloatingMenu
              selectedItems={selectedItems}
              handleButtonsActions={handleFloatingModalNavigation}
            />
          )}
        </>
      )}
      {renderConfirmationScreen}
    </main>
  )
}
